import React, { useState, useEffect } from 'react';
import { getDevices, getPositions } from '../traccar';
import { assessHealth, healthScore, HEALTH_COLORS } from '../vehicleHealth';
import { ackHealth, unackHealth, isAcked, resolvedDisplay, mergeServerAcks } from '../healthAck';
import { playOpen, playClose } from '../clickSound';

const SCORE_LABEL = {
  good: { text: 'HEALTHY', bg: '#f0fdf4', color: '#15803d', dot: '#22c55e' },
  warn: { text: 'NEEDS ATTENTION', bg: '#fffbeb', color: '#b45309', dot: '#f59e0b' },
  bad: { text: 'PROBLEM DETECTED', bg: '#fef2f2', color: '#b91c1c', dot: '#ef4444' },
};

// native app = dark glass skin; show the customer's own name for each car
const IS_NATIVE = typeof window !== 'undefined' && !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());
const dName = (d) => (d ? ((d.attributes || {}).displayName || d.name) : '');

export default function HealthPage() {
  const [devices, setDevices] = useState([]);
  const [positions, setPositions] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [error, setError] = useState(null);
  const [ackTick, setAckTick] = useState(0); // re-render after resolve/undo

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [devs, poss] = await Promise.all([getDevices(), getPositions()]);
        if (cancelled) return;
        mergeServerAcks(devs); // pull in resolves made on other profiles/tabs
        setDevices(devs);
        setPositions(poss);
        setError(null);
      } catch (e) {
        if (!cancelled) setError(e.message);
      }
    };
    load();
    const t = setInterval(load, 10000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>🩺 Vehicle Health</h1>
        <p>{error ? `🔴 ${error}` : 'Live condition of every system, updated every 10 seconds — green means verified healthy, not just "no news". Click a vehicle to open its full check-up.'}</p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxWidth: '860px' }}>
        {devices.map((d) => {
          const p = positions.find((x) => x.deviceId === d.id);
          const checks = p ? assessHealth(p.attributes) : [];
          // a resolved (acked) problem no longer counts against the overall score
          const score = healthScore(checks.map((c) => (((c.level === 'bad' || c.level === 'warn') && c.ackKey && isAcked(d.id, c.ackKey, c.ackSig)) ? { ...c, level: 'good' } : c)));
          const s = score ? SCORE_LABEL[score] : null;
          const open = openId === d.id;
          // readings are only as fresh as the last record; flag stale snapshots so
          // a resolved issue (e.g. codes cleared at a shop) isn't read as live.
          const lastSeen = p ? Math.max(0, ...[p.serverTime, p.deviceTime, p.fixTime].map((t) => (t ? new Date(t).getTime() : 0))) : 0;
          const stale = lastSeen ? (Date.now() - lastSeen > 15 * 60 * 1000) : false;

          return (
            <div key={d.id} style={IS_NATIVE ? {
              background: 'linear-gradient(160deg, rgba(255,255,255,0.085), rgba(255,255,255,0.035))',
              border: '1px solid rgba(255,255,255,0.10)', borderRadius: '20px', overflow: 'hidden',
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.07)', color: '#e8ebf5',
            } : {
              background: 'white', borderRadius: '12px', overflow: 'hidden',
              border: '1px solid transparent',
              backgroundImage: 'linear-gradient(white, white), linear-gradient(135deg, rgba(15,23,42,0.4), rgba(249,115,22,0.55), rgba(255,255,255,0.9))',
              backgroundOrigin: 'padding-box, border-box',
              backgroundClip: 'padding-box, border-box',
              boxShadow: open ? '0 6px 18px rgba(15,23,42,0.12)' : '0 1px 3px rgba(15,23,42,0.07)',
            }}>
              <div
                onClick={() => { (open ? playClose : playOpen)(); setOpenId(open ? null : d.id); }}
                style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px', cursor: 'pointer' }}
              >
                <span style={{
                  width: '12px', height: '12px', borderRadius: '50%', flexShrink: 0,
                  background: s ? s.dot : '#cbd5e1',
                  boxShadow: s ? `0 0 8px ${s.dot}66` : 'none',
                }}></span>
                <span style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '15px', color: IS_NATIVE ? '#e8ebf5' : '#0f172a', flex: 1 }}>
                  {dName(d)}
                </span>
                {s && (
                  <span style={{ fontSize: '11px', fontWeight: 700, padding: '3px 12px', borderRadius: '999px', background: stale ? '#f1f5f9' : s.bg, color: stale ? '#64748b' : s.color, letterSpacing: '0.5px' }}>
                    {stale ? 'LAST KNOWN' : s.text}
                  </span>
                )}
                <span style={{ color: '#94a3b8', fontSize: '13px', transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }}>›</span>
              </div>

              {open && (
                <div style={{ padding: '4px 16px 16px', borderTop: '1px solid #f1f5f9' }}>
                  {p && (
                    <p style={{ fontSize: '12px', color: '#94a3b8', margin: '10px 0' }}>
                      Last report: {new Date(p.fixTime).toLocaleString()}
                    </p>
                  )}
                  {stale && (
                    <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '10px 12px', margin: '0 0 12px', fontSize: '12px', color: '#92400e' }}>
                      ⏱ These readings are from the last report, not live. If you just had the car serviced, <b>start it and drive for a minute</b> — the tracker will send a fresh reading and any resolved issues (like a cleared check-engine code) will update automatically.
                    </div>
                  )}
                  {checks.length === 0 && <p className="text-muted">No data received from this vehicle yet.</p>}
                  <div className="grid grid-3" style={{ gap: '10px' }}>
                    {checks.map((c) => {
                      const canResolve = (c.level === 'bad' || c.level === 'warn') && c.ackKey;
                      const resolved = canResolve && isAcked(d.id, c.ackKey, c.ackSig);
                      const rd = resolved ? resolvedDisplay(c.ackKey) : undefined;
                      if (resolved && rd === null) return null; // no healthy-state card (e.g. warning-light history)
                      const col = resolved ? HEALTH_COLORS.good : HEALTH_COLORS[c.level];
                      const verdict = resolved && rd ? rd.verdict : c.verdict;
                      const detail = resolved && rd ? rd.detail : c.detail;
                      return (
                        <div key={c.title} style={{
                          background: col.bg, borderLeft: `4px solid ${col.border}`, borderRadius: '10px', padding: '12px 14px',
                        }}>
                          <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '3px' }}>
                            {resolved ? '✅' : c.icon} {c.title}
                          </div>
                          <div style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, fontSize: '14px', color: col.text }}>{verdict}</div>
                          <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>{detail}</div>
                          {canResolve && !resolved && (
                            <button onClick={() => { ackHealth(d.id, c.ackKey, c.ackSig); setAckTick((n) => n + 1); }} style={{ marginTop: '8px', background: 'white', color: '#15803d', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '5px 10px', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}>✓ Mark resolved</button>
                          )}
                          {resolved && (
                            <button onClick={() => { unackHealth(d.id, c.ackKey); setAckTick((n) => n + 1); }} style={{ marginTop: '8px', background: 'transparent', color: '#94a3b8', border: 'none', fontSize: '11px', fontWeight: 600, cursor: 'pointer', padding: '4px 0' }}>Undo</button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
